import React, { useState, useEffect, useCallback } from 'react';
import { FileCheck, Eye, Download } from 'lucide-react';

// Retained copy of the driver's SIGNED vehicle lease(s), shown in My Van so they
// always have their executed agreement to hand. The signed PDF lives in the
// private vehicle-docs store; the driver pulls their own copy via the
// owner-gated getSignedContractFile action. View / Download only — no re-signing.

const CONTRACT_URL = '/.netlify/functions/vehicle-contract-sb';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}
function b64ToBlob(base64, type) {
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  return new Blob([bytes], { type: type || 'application/pdf' });
}

export default function SignedLeasesCard({ shift }) {
  const [rows, setRows]   = useState(null);
  const [busy, setBusy]   = useState('');
  const [error, setError] = useState('');

  const api = useCallback(async (payload) => {
    const res = await fetch(CONTRACT_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  }, []);

  useEffect(() => {
    let alive = true;
    api({ action: 'getMySignedContracts' })
      .then(d => { if (alive) setRows(d && d.success ? (d.contracts || []) : []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [api]);

  const fetchBytes = async (c) => {
    const d = await api({ action: 'getSignedContractFile', id: c.id });
    if (!d || !d.success) throw new Error(d?.error || 'Could not open the signed lease');
    return { blob: b64ToBlob(d.base64, d.contentType), filename: d.filename };
  };

  const onView = async (c) => {
    const w = window.open('', '_blank');
    setBusy(c.id + ':vw'); setError('');
    try {
      const { blob } = await fetchBytes(c);
      const url = URL.createObjectURL(blob);
      if (w) w.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (w) w.close(); setError(e.message); } finally { setBusy(''); }
  };

  const onDownload = async (c) => {
    setBusy(c.id + ':dl'); setError('');
    try {
      const { blob, filename } = await fetchBytes(c);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename || `Signed-Lease-${c.rego}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  };

  if (!rows || !rows.length) return null;   // nothing signed → show nothing

  return (
    <div style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', padding: 16, marginBottom: 16, background: 'var(--hp-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <FileCheck size={16} color="var(--hp-green)" />
        <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>Your signed lease {rows.length > 1 ? 'agreements' : 'agreement'}</span>
      </div>
      {error && <div style={{ color: 'var(--hp-red)', fontSize: '0.8rem', marginBottom: 8 }}>{error}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map(c => (
          <div key={c.id} style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 12 }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--hp-text)' }}>
              Vehicle Lease — {c.rego}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 3 }}>
              {c.weeklyFee ? `$${Number(c.weeklyFee).toFixed(2)}/wk · ` : ''}Signed {fmtDate(c.signedAt)}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button onClick={() => onView(c)} disabled={busy === c.id + ':vw'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 12px', fontSize: '0.8rem', fontWeight: 700, border: 'none', borderRadius: 'var(--radius)', background: 'var(--hp-gold)', color: '#1a1a2e', cursor: 'pointer', opacity: busy === c.id + ':vw' ? 0.6 : 1 }}>
                <Eye size={13} /> {busy === c.id + ':vw' ? '…' : 'View'}
              </button>
              <button onClick={() => onDownload(c)} disabled={busy === c.id + ':dl'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 12px', fontSize: '0.8rem', fontWeight: 700, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)', color: 'var(--hp-text)', cursor: 'pointer', opacity: busy === c.id + ':dl' ? 0.6 : 1 }}>
                <Download size={13} /> {busy === c.id + ':dl' ? '…' : 'Download'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
